import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import ProtectedRoute from 'uplisting-frontend/pods/base/protected-route';
import type ProtectionOverviewController from 'uplisting-frontend/pods/protection/overview/controller';
import { type BookingWindow } from 'uplisting-frontend/utils/filters/types';

const PERMISSIONS = ['dashboard.index', 'advanced_report_configurations.index'];

interface IParams extends Record<string, unknown> {
  propertyIds: string[];
  protectionStatuses: string[];
  page: number;
  window?: BookingWindow;
}

export default class ProtectionOverviewRoute extends ProtectedRoute {
  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @cached
  get isPermitted(): boolean {
    return PERMISSIONS.some((permission) =>
      this.currentUser.hasAccessTo(permission),
    );
  }

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);

    if (this.isPermitted) {
      return;
    }

    transition.abort();

    if (this.currentUser.hasAccessTo('disputes.index')) {
      this.router.transitionTo('protection.disputes');
    } else {
      this.router.transitionTo('index');
    }
  }

  async model(params: IParams): Promise<void> {
    const controller = this.controllerFor(
      'protection.overview',
    ) as ProtectionOverviewController;

    const { window, ...rest } = params;

    Object.assign(controller, rest);

    if (window !== undefined) {
      controller.bookingWindow = window;
    }

    await Promise.all([
      this.propertyRepository.findAll(),
      controller.fetchData(),
    ]);
  }

  afterModel(_resolvedModel: unknown, transition: Transition): void {
    const controller = this.controllerFor(
      'protection.overview',
    ) as ProtectionOverviewController;

    const requested = Number(transition.to?.queryParams?.['page'] ?? 1);

    if (controller.totalPages > 0 && requested > controller.totalPages) {
      const page = controller.totalPages;

      void transition
        .then(() => {
          if (this.router.currentRouteName?.startsWith('protection.overview')) {
            this.router.replaceWith('protection.overview', {
              queryParams: { page },
            });
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {});
    }
  }

  resetController(
    controller: ProtectionOverviewController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      controller.occasions.forEach((occasion) => {
        if (occasion.isDestroyed) {
          return;
        }

        occasion.unloadRecord();
      });

      controller.resetFilters();
      controller.occasions = [];
      controller.searchQuery = '';
      controller.page = 1;
      controller.totalPages = 0;
    }
  }
}
